import { RefObject, useEffect, useRef, useState } from 'react';
import { Box, CircularProgress } from '@mui/material';
import ArrowDownwardRounded from '@mui/icons-material/ArrowDownwardRounded';
import { isTextEntry } from './useKeyboardOpen';

/** How far (after resistance) the page must be pulled to refresh. */
const THRESHOLD = 64;
const MAX_PULL = 96;

/** Whether the touch began inside something that scrolls on its own and isn't at its top. */
function innerScrollerOwnsGesture(target: EventTarget | null, root: HTMLElement): boolean {
  for (let node = target as HTMLElement | null; node && node !== root; node = node.parentElement) {
    if (node.scrollTop > 0) return true;
  }
  return false;
}

/**
 * Pull to refresh for the phone app shell. An installed web app has no reload
 * button, so pulling the page down from its top reloads it, with the usual
 * spinner dropping in from the header. It renders a zero-height sticky strip at
 * the top of the scroll area, so it never takes space or moves the page.
 */
export default function PullToRefresh({ scrollRef }: { scrollRef: RefObject<HTMLElement> }) {
  const [pull, setPull] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const pullRef = useRef(0);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return undefined;
    let startY: number | null = null;
    let startX = 0;
    let pulling = false;

    const setDistance = (d: number) => {
      pullRef.current = d;
      setPull(d);
    };
    const onStart = (e: TouchEvent) => {
      startY = null;
      pulling = false;
      // No pull while typing: a reload would drop what's being written.
      if (e.touches.length !== 1 || el.scrollTop > 0 || isTextEntry(document.activeElement)) return;
      if (innerScrollerOwnsGesture(e.target, el)) return;
      startY = e.touches[0].clientY;
      startX = e.touches[0].clientX;
    };
    const onMove = (e: TouchEvent) => {
      if (startY === null) return;
      const dy = e.touches[0].clientY - startY;
      const dx = e.touches[0].clientX - startX;
      if (!pulling) {
        // Only a clear downward drag starts a pull; anything else is a scroll or a swipe.
        if (dy < 0 || Math.abs(dx) > Math.abs(dy)) {
          startY = null;
          return;
        }
        if (dy < 10) return;
        pulling = true;
      }
      if (e.cancelable) e.preventDefault();
      setDistance(Math.min(MAX_PULL, (dy - 10) * 0.5));
    };
    const onEnd = () => {
      if (pulling && pullRef.current >= THRESHOLD) {
        setRefreshing(true);
        setDistance(THRESHOLD);
        window.location.reload();
      } else {
        setDistance(0);
      }
      startY = null;
      pulling = false;
    };

    el.addEventListener('touchstart', onStart, { passive: true });
    el.addEventListener('touchmove', onMove, { passive: false });
    el.addEventListener('touchend', onEnd);
    el.addEventListener('touchcancel', onEnd);
    return () => {
      el.removeEventListener('touchstart', onStart);
      el.removeEventListener('touchmove', onMove);
      el.removeEventListener('touchend', onEnd);
      el.removeEventListener('touchcancel', onEnd);
    };
  }, [scrollRef]);

  const visible = pull > 0 || refreshing;
  const progress = Math.min(1, pull / THRESHOLD);

  return (
    <Box
      aria-hidden
      sx={{ position: 'sticky', top: 0, height: 0, zIndex: 5, display: 'flex', justifyContent: 'center', pointerEvents: 'none' }}
    >
      <Box
        data-testid="pull-to-refresh"
        sx={{
          width: 36,
          height: 36,
          mt: '-8px',
          borderRadius: '50%',
          bgcolor: '#1C1C1C',
          border: '1px solid #2A2A2A',
          boxShadow: '0 4px 12px rgba(0,0,0,0.4)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          opacity: visible ? Math.max(progress, refreshing ? 1 : 0) : 0,
          transform: `translateY(${pull}px)`,
          transition: pull === 0 || refreshing ? 'transform 200ms ease, opacity 200ms ease' : 'none'
        }}
      >
        {refreshing ? (
          <CircularProgress size={18} thickness={5} sx={{ color: '#F79420' }} />
        ) : (
          <ArrowDownwardRounded
            sx={{
              fontSize: 20,
              color: progress >= 1 ? '#F79420' : '#B6B6B6',
              transform: `rotate(${progress >= 1 ? 180 : 0}deg)`,
              transition: 'transform 160ms ease, color 160ms ease'
            }}
          />
        )}
      </Box>
    </Box>
  );
}
